import type { JSX, ReactNode } from "react";
import type { UseQueryResult } from "@tanstack/react-query";

import { cn } from "../../lib/utils.js";
import { Card } from "../ui/card.js";

/** What a dashboard panel needs from its query. */
export type PanelQuery<T> = Pick<UseQueryResult<T>, "data" | "isLoading" | "isError">;

/**
 * The surface every dashboard panel shares: a title row (with an optional
 * control on the right) and a body that gives way to a plain loading / error /
 * empty message — so short or missing history never renders a blank chart.
 */
export function PanelCard({
  title,
  action,
  isLoading,
  isError,
  empty,
  className,
  children,
}: {
  title: string;
  action?: ReactNode;
  isLoading: boolean;
  isError: boolean;
  /** Shown instead of the body when there is nothing to draw. */
  empty?: string | null;
  className?: string;
  children: ReactNode;
}): JSX.Element {
  let body: ReactNode = children;
  if (isLoading) body = <p className="text-muted-foreground text-sm">Loading…</p>;
  else if (isError) body = <p className="text-bad text-sm">Failed to load.</p>;
  else if (empty) body = <p className="text-muted-foreground text-sm">{empty}</p>;
  return (
    <Card className={cn("gap-3 p-5", className)}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-sm font-medium">{title}</div>
        {action}
      </div>
      {body}
    </Card>
  );
}
